import React, { useEffect, useRef, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { embedDashboard } from "@superset-ui/embedded-sdk";
import useAuth from "../../../hooks/useAuth";
import { occyLs } from "../../../utils/localStorage";
import styled from "styled-components";
import { toast } from "react-toastify";
import { StyledLabel, Wrap } from "../../../assets/styledComponents/Global";
import GridSkeleton from "../../job/skeleton/GridSkeleton";
import EmptyState from "../../../app/components/nodata/EmptyState";
import PortletHeader from "../../../app/components/portlet/PortletHeader";
import StyledButton from "../../../app/components/styledComponents/StyledButton";
import Portlet from "../../../app/components/portlet/Portlet";
import FormikSelect from "../../../app/components/formikComponents/FormikSelect";
import { Form, Formik } from "formik";
import RecruitmentSidebar from "./Sidebar/RecruitmentSidebar";
import { Helmet } from 'react-helmet-async';
import {
  PERMISSION_DENIED_MESSAGE,
  PERMISSIONS,
  usePermissionActionGuard,
} from '../../../permission-system';

// --------- API Helpers ---------
async function fetchConfig() {
  const token = occyLs.getObject("token");
  const res = await fetch(`${process.env.REACT_APP_API_URL}/api/superset/config`, {
    credentials: 'include',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) throw new Error("Failed to load config");
  return res.json();
}

async function fetchToken(body) {
  const token = occyLs.getObject("token");
  const res = await fetch(`${process.env.REACT_APP_API_URL}/api/superset/guest-token`, {
    method: "POST",
    credentials: 'include',
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Failed to get guest token");
  return res.json();
}

// --------- Component ---------
function getDefaultDashboardId() {
  const raw = process.env.REACT_APP_ANALYTICS_DASHBOARD_IDS;
  if (!raw || typeof raw !== "string") return 39;
  const first = parseInt(raw.split(",")[0].trim(), 10);
  return Number.isNaN(first) ? 39 : first;
}

export default function SupersetEmbed({ session }) {
  const { can } = usePermissionActionGuard();
  const canManageReportAnalytics = can(PERMISSIONS.MANAGE_REPORT_ANALYTICS);
  const { dashboardId: dashboardIdParam } = useParams();
  const navigate = useNavigate();
  const [activeDashboard, setActiveDashboard] = useState(null);
  const [activeDashboardLabel, setActiveDashboardLabel] = useState(null);
  const [config, setConfig] = useState(null);
  const [selectedId, setSelectedId] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true); // only for config
  const [iframeLoading, setIframeLoading] = useState(true); // for iframe/dashboard

  const mountRef = useRef(null);
  const dashboardRef = useRef(null);
  const { user } = useAuth();
  const userRef = useRef({ firstName: user?.firstName, companyId: user?.company?.id });
  const defaultDashboardId = useMemo(() => getDefaultDashboardId(), []);
  const sidebarDashboards = useMemo(
    () => config?.dashboards ?? [],
    [config?.dashboards]
  );

  const supersetDomain = config?.supersetDomain;
  const themeApplied = occyLs.getItem("Theme");

  // --------- Load config ---------
  useEffect(() => {
    const loadData = async () => {
      setError("");
      try {
        const cfg = await fetchConfig();
        setConfig(cfg);
      } catch (e) {
        setError(String(e));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user?.company?.id, session.tenantId]);


  // Keep userRef current so fetchGuestToken always uses up-to-date values
  // without those values being in the embed effect's dependency array.
  useEffect(() => {
    userRef.current = { firstName: user?.firstName, companyId: user?.company?.id };
  }, [user?.firstName, user?.company?.id]);

  // --------- Sync dashboard from URL (or default) when config or param changes ---------
  useEffect(() => {
    if (!config?.dashboards?.length) return;
    const id = dashboardIdParam ? parseInt(dashboardIdParam, 10) : defaultDashboardId;
    const dash = config.dashboards.find((d) => d.id === id);
    const resolvedDash = dash || config.dashboards.find((d) => d.id === defaultDashboardId) || config.dashboards[0];
    if (!resolvedDash) return;
    const resolvedId = resolvedDash.id;
    setActiveDashboard(resolvedId);
    setSelectedId(resolvedDash.embedded_id || "");
    if (!dashboardIdParam || !dash) {
      navigate(`/analytics/${resolvedId}`, { replace: true });
    }
  }, [config, dashboardIdParam, defaultDashboardId, navigate]);

   // --------- Embed dashboard ---------
  useEffect(() => {
    if (!selectedId || !supersetDomain) return;

    let cancelled = false;
    let resizeCleanup = null;
    let loadingTimer = null;

    const renderDashboard = async () => {
      if (!mountRef.current) return;

      try {
        setIframeLoading(true);
        mountRef.current.innerHTML = "";

        const dashboard = await embedDashboard({
          id: String(selectedId),
          supersetDomain,
          mountPoint: mountRef.current,
          debug: process.env.NODE_ENV !== "production",
          fetchGuestToken: async () => {
            const { token } = await fetchToken({
              userId: Date.now().toString(),
              userName: userRef.current.firstName,
              tenantId: userRef.current.companyId,
              dashboardId: selectedId,
            });
            return token;
          },
          dashboardUiConfig: {
            hideTitle: false,
            hideTab: false,
            hideChartControls: false,
            filters: { expanded: false },
            theme: themeApplied,
          },
          urlParams: { standalone: "true" },
        });

        if (cancelled) {
          dashboard.unmount();
          return;
        }

        dashboardRef.current = dashboard;

        const systemPrefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
        if (themeApplied === "light" && systemPrefersDark) {
          dashboard.setThemeConfig({
            theme_default: {
              token: {
                fontFamily: "Raleway, sans-serif",
                colorPrimary: "#dc601e",
                colorInfo: "#dc601e",
                borderRadius: 6,
                colorBgBase: "#ffffff",
                colorBgContainer: "#ffffff",
                colorBgLayout: "#ffffff",
                headerBg: "#fff",
                bodyBg: "#fff",
                footerBg: "#fff",
                headerColor: "#fff",
              },
              algorithm: "light",
            },
          });
        }

        const iframe = mountRef.current?.querySelector("iframe");
        if (iframe) {
          const adjustHeight = () => {
            if (!mountRef.current) return;
            const topOffset = mountRef.current.getBoundingClientRect().top;
            const newHeight = window.innerHeight - topOffset - 20;
            iframe.style.height = `${newHeight}px`;
          };

          iframe.style.width = "100%";
          iframe.style.border = "none";
          iframe.style.overflow = "hidden";
          iframe.style.borderRadius = "5px";

          adjustHeight();
          window.addEventListener("resize", adjustHeight);
          resizeCleanup = () => window.removeEventListener("resize", adjustHeight);

          loadingTimer = setTimeout(() => {
            if (!cancelled) setIframeLoading(false);
          }, 3000);
        } else {
          setIframeLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError("Failed to render dashboard");
          toast.error(e?.message || "Failed to render dashboard");
          setIframeLoading(false);
        }
      }
    };

    renderDashboard();

    return () => {
      cancelled = true;
      resizeCleanup?.();
      clearTimeout(loadingTimer);
      if (dashboardRef.current) {
        dashboardRef.current.unmount();
        dashboardRef.current = null;
      }
      if (mountRef.current) {
        mountRef.current.innerHTML = "";
      }
    };
  }, [selectedId, supersetDomain, themeApplied]);

  // --------- Render UI ---------
  const pageTitle = activeDashboardLabel ? `${activeDashboardLabel} | Occy App` : "Occy App";

  if (!canManageReportAnalytics) {
    return (
      <StyledDiv>
        <Helmet><title>{pageTitle}</title></Helmet>
        <div className="d-flex align-items-center justify-content-center p-5 min-vh-50">
          <div className="text-center">
            <h3 className="mb-3">Access denied</h3>
            <p className="text-muted mb-0">{PERMISSION_DENIED_MESSAGE}</p>
          </div>
        </div>
      </StyledDiv>
    );
  }

  if (loading)
    return (
      <StyledDiv>
        <Helmet><title>{pageTitle}</title></Helmet>
        <EmbedContainer>
          <GridSkeleton />
        </EmbedContainer>
      </StyledDiv>
    );


  return (
       <>
       <Helmet><title>{pageTitle}</title></Helmet>
       <Portlet className="m-0">
      <PortletHeader
        title={<StyledLabel size='32px' weight={'500'} font='Fredoka, sans-serif'>Analytics</StyledLabel>}
        titleDetails={<StyledLabel size='14px' weight={'400'} font='Raleway, sans-serif'>Explore recruiting insights for your organisation</StyledLabel>}
        labelClassName='pb-3'
        containerClassName='mb-3 px-4 pt-3 bg-white rounded'
        actions={<Styleddiv className="d-flex justify-content-center align-items-center gap-3">
         {config?.dashboards?.length > 100 && <Formik initialValues={{dashboard:selectedId || ''}}>
             {({ setFieldValue }) => (
            <Form>
            <FormikSelect
                      name="dashboard"
                      id="dashboard"
                      placeholder="Select Dashboard"
                      className='border-primary'
                      options={config?.dashboards?.filter(dash => dash?.embedded_id?.length > 0)?.map((dash) => ({
                        label: dash?.title,
                        value: dash?.embedded_id,
                      }))}
                      onChange={(values) => {
                        setFieldValue("dashboard", values.value)
                        setSelectedId(values.value)}}
                    />
                    
                    </Form>
                    )}
                    </Formik>}
          <StyledButton
            onClick={() => {
              window.location.href = '/schedule/reporting';
            } }
            className="btn btn-primary"
          >
            Schedule Reports
          </StyledButton>
        </Styleddiv>} />
</Portlet>
{error ? (<Portlet className="px-3 py-5"> <Wrap className="d-flex justify-content-center align-content-center align-items-center h-100">
        <div className="text-center px-4">
          <EmptyState
            title="No Dashboard"
            description="There are currently no dashboards to display in this section."
          />
        </div>
      </Wrap></Portlet>) : (
    <StyledDiv className="">
        <DashboardLayout>
  <StyledSide className="sidebar">
    <RecruitmentSidebar
      activeDashboardId={activeDashboard}
      onClick={(id) => navigate(`/analytics/${id}`)}
      dashboards={sidebarDashboards}
      onActiveLabel={setActiveDashboardLabel}
    />
  </StyledSide>

  <StyledMain className="dashboard-content">
    <EmbedContainer className="rounded" ref={mountRef} />

    {iframeLoading && (
        <LoaderOverlay>
            <GridSkeleton />
          </LoaderOverlay>
    )}
  </StyledMain>
</DashboardLayout>

      </StyledDiv>)}</>
  );
}

// --------- Styles ---------

const DashboardLayout = styled.div`
  display: flex;
  height: 100vh;
  width: 100%;
  background: ${({ theme }) => (theme?.dark ? "#121212" : "#f6f7fb")};
  overflow: hidden;
`;

const EmbedContainer = styled.div`
  width: 100%;
  height: 100%;
  background: ${({ theme }) => (theme?.dark ? theme?.white : "#ffffff")};
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const StyledMain = styled.main`
 position: relative;
  flex: 1;
  padding: 16px;
  background: ${({ theme }) =>
    theme?.dark ? '#171717' : '#ffffff'};
  overflow: hidden;
`;

const StyledSide = styled.aside`
  display: flex;
  flex-direction: column;
  min-height: 0;
  width: 300px;
  min-width: 300px;
  padding: 14px;
  background: ${({ theme }) =>
    theme?.dark ? '#171717' : 'rgba(255, 255, 255, 0.75)'};
  backdrop-filter: blur(14px) saturate(160%);
  -webkit-backdrop-filter: blur(14px) saturate(160%);
  border-right: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow:
    0 20px 40px rgba(0, 0, 0, 0.06),
    inset -1px 0 0 rgba(255, 255, 255, 0.6);
  overflow: hidden;
`;

const StyledDiv = styled.div`
  width: 100%;
  height: 100vh;
  position: relative;
  display: flex;
  overflow: hidden;
  margin-top: -14px;

  iframe {
    width: 100%;
    border: none;
    overflow: hidden;
  }
`;

const LoaderOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme }) =>
    theme?.dark ? theme?.darkThemBoxBgColor : "#fff"};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Styleddiv = styled.div`
  .async-select [class$="-control"] {
    border: 1px solid ${({ theme }) => theme.primary} !important;
    border-radius: 50px;
    
    label {
      font-weight: 500 !important;
      font-size: 14px !important;
      font-family: 'Raleway', sans-serif;
    }
    
    label, svg {
      color: ${({ theme }) => theme.primary} !important;
    }
  }
`;